import React from 'react'
import { Box, Typography } from '@mui/material'
import { PEdge } from '../../../types/carbon/models/PEdge'
import { SettingsSwitch, SettingsSelect, SettingsNumberInput, SettingsTextField, SettingsRow } from './SettingsControls'

interface EdgeSectionProps {
  label: string
  edge: PEdge
  onChange: (edge: PEdge) => void
}

const sortBandOptions = [
  { value: 'First', label: 'First displayed' },
  { value: 'Freq', label: 'Frequency' },
  { value: 'ColPC', label: 'Column %' },
  { value: 'RowPC', label: 'Row %' },
]

const sortTypeOptions = [
  { value: 'Value', label: 'Value' },
  { value: 'Code', label: 'Code' },
  { value: 'Label', label: 'Label' },
]

const EdgeSection: React.FC<EdgeSectionProps> = ({ label, edge, onChange }) => {
  const update = (patch: Partial<PEdge>) => onChange({ ...edge, ...patch })

  const sort = edge.sort || { active: false, increasing: true, ungrouped: false, band: 'First' as const, type: 'Value' as const, key: 0 }
  const hide = edge.hide || { active: false, missing: false, empty: false }
  const isRow = label === 'Row'
  const vectorsPlaceholder = isRow ? 'Rows to hide e.g. 1;3;4;7' : 'Columns to hide e.g. 1;3;4;7'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* Sort */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Sort</Typography>
      <SettingsSwitch label="Sort Active" checked={sort.active} onChange={v => update({ sort: { ...sort, active: v } })} />
      {sort.active && (
        <>
          <SettingsRow>
            <SettingsSwitch label="Increasing" checked={sort.increasing} onChange={v => update({ sort: { ...sort, increasing: v } })} />
            <SettingsSwitch label="Ungrouped" checked={sort.ungrouped} onChange={v => update({ sort: { ...sort, ungrouped: v } })} />
          </SettingsRow>
          <SettingsRow>
            <SettingsSelect label="Type" value={sort.type} options={sortTypeOptions} onChange={v => update({ sort: { ...sort, type: v as typeof sort.type } })} />
            <SettingsSelect label="Band" value={sort.band} options={sortBandOptions} onChange={v => update({ sort: { ...sort, band: v as typeof sort.band } })} />
          </SettingsRow>
          <SettingsNumberInput label="Key" value={sort.key} onChange={v => update({ sort: { ...sort, key: v } })} min={0} max={999} />
        </>
      )}

      {/* Hide */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Hide</Typography>
      <SettingsSwitch label="Hide Active" checked={hide.active} onChange={v => update({ hide: { ...hide, active: v } })} />
      {hide.active && (
        <>
          <SettingsRow>
            <SettingsSwitch label="If data missing" checked={hide.missing} onChange={v => update({ hide: { ...hide, missing: v } })} />
            <SettingsSwitch label="If data equal to zero" checked={hide.empty} onChange={v => update({ hide: { ...hide, empty: v } })} />
          </SettingsRow>
          <SettingsTextField label="Vectors" placeholder={vectorsPlaceholder} value={hide.vectors || ''} onChange={v => update({ hide: { ...hide, vectors: v } })} />
        </>
      )}

      {/* Base Count */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Other</Typography>
      <SettingsSwitch label="Base Count" checked={edge.baseCount} onChange={v => update({ baseCount: v })} />

      {/* Labels */}
      {edge.labels && (
        <>
          <SettingsSwitch label="Labels Visible" checked={edge.labels.visible} onChange={v => update({ labels: { ...edge.labels!, visible: v } })} />
        </>
      )}

      {/* Groups */}
      {edge.groups && (
        <SettingsSwitch label="Groups Visible" checked={edge.groups.visible} onChange={v => update({ groups: { ...edge.groups!, visible: v } })} />
      )}
    </Box>
  )
}

export default EdgeSection
